// @flow
import * as React from 'react';

import useSession from 'common/hooks/useSession';
import useClient from 'common/hooks/useClient';
import ErrorReporter from 'common/utils/ErrorReporter';
import NewRelic from 'common/utils/NewRelic';
import { useLocation } from 'react-router-dom';
import ProductTrackerService from 'common/services/ProductTrackerService';

import { usePlatformSettings } from 'common/modules/platform-settings';

type Props = { children: React.Node };

const AppExternalServiceInitializer = ({ children }: Props): React.Node => {
    const session = useSession();
    const client = useClient();
    const platformSettings = usePlatformSettings();
    const location = useLocation();

    React.useEffect(() => {
        const {
            personnelId,
            clientId: personnelClientId,
            name: personnelName,
            timezone,
            language
        } = session.personnel;

        NewRelic.trackData({
            clientId: client.clientId,
            personnelId: `${personnelClientId}/${personnelId}`
        });

        // Error Reporter Service
        ErrorReporter.setUser({
            id: `${personnelClientId}/${personnelId}`,
            clientId: client.clientId
        });

        // Pendo service
        if (
            typeof pendo !== 'undefined' ||
            (pendo.isReady && !pendo.isReady())
        ) {
            pendo.initialize({
                apiKey: '216a6eb9-de3e-42aa-4d91-852953239f06',

                visitor: {
                    id: `${personnelClientId}/${personnelId}`, // Required if user is logged in
                    name: personnelName,
                    timezone,
                    personnelLanguage: language
                },

                account: {
                    id: personnelClientId,
                    name: personnelClientId,
                    // only send the following if user is not mara
                    ...(!session.personnel.isGlobalPersonnel()
                        ? {
                              workflowType:
                                  platformSettings.analyticsDashboardPreferences
                                      .workflowType,
                              stage: client.stage,
                              enforceNewLiveView:
                                  client.liveViewVisibilitySettings
                                      .enforceNewLiveView
                          }
                        : null)
                },

                //in regression we want pendo guides to be disabled
                disableGuides:
                    window.localStorage.getItem('disablePendoGuides') === 'true'
                        ? true
                        : false
            });
        }
    }, [session, client, platformSettings]);

    React.useEffect(() => {
        NewRelic.track('product', ProductTrackerService.getActiveProduct());
    }, [location]);

    React.useEffect(() => {
        // eslint-disable-next-line no-undef
        NewRelic.track('version', __VERSION__);
    }, []);

    return children;
};

export default AppExternalServiceInitializer;
